import React, { ReactNode, useRef, useState } from 'react';

import { Button } from '../Clickables/Buttons/Button';
import {
  OverflowDirection,
  useElementIsOverflowing,
} from '../../helpers/hooks/useElementIsOverflowing';
import { twMerge } from 'tailwind-merge';

interface CollapsibleTextProps {
  children: ReactNode;
  maxLinesNumber?: number;
  showMoreButtonLabel?: string;
  showLessButtonLabel?: string;
  className?: string;
}

/**
 * A component that displays text with a collapsible feature. It shows a limited number of lines initially
 * and provides a button to expand and optionally collapse the text.
 *
 * @param {ReactNode} children - The text content to be displayed within the collapsible component.
 * @param {number} [maxLinesNumber=2] - The maximum number of lines to display before collapsing the text.
 * @param {string} [showMoreButtonLabel='read more'] - The label for the button to expand the text.
 * @param {string} [showLessButtonLabel] - The label for the button to collapse the text.
 * @param {string} [className] - Additional CSS classes to apply to the component.
 *
 * @returns {JSX.Element} The rendered CollapsibleText component.
 */
export const CollapsibleText = ({
  children,
  maxLinesNumber = 2,
  showMoreButtonLabel = 'read more',
  showLessButtonLabel,
  className,
}: CollapsibleTextProps) => {
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useElementIsOverflowing(containerRef, OverflowDirection.Y, [], 1);

  const handleReadMoreClick = (): void => {
    setIsTextExpanded(previousState => !previousState);
  };

  return (
    <span className={twMerge('text-left break-words whitespace-pre-wrap', className)}>
      <span
        ref={containerRef}
        className={!isTextExpanded ? `line-clamp-${maxLinesNumber} overflow-hidden` : ''}
      >
        {children}
      </span>
      {isOverflowing && isTextExpanded && showLessButtonLabel && (
        <Button
          className="body-text-sm text-tuna-shade p-0! font-semibold"
          onClick={handleReadMoreClick}
          variant="buttonNone"
        >
          {showLessButtonLabel}
        </Button>
      )}
      {isOverflowing && showMoreButtonLabel && !isTextExpanded && (
        <Button
          className="body-text-sm text-tuna-shade p-0! font-semibold"
          onClick={handleReadMoreClick}
          variant="buttonNone"
        >
          {showMoreButtonLabel}
        </Button>
      )}
    </span>
  );
};
